import React, { useEffect, useMemo, useRef, useState } from 'react';

import AccordionViewV3 from 'components/AccordionViewV3/AccordionViewV3';
import { useGroupOperationsSSE } from 'hooks/useGroupOperationsSSE';
import { GroupOperationJob } from 'redux/types/GroupOperations.types';
import { useAppSelector } from 'utils/hooks/useAppSelector';

import { YbCoreSnackbar } from '@yubi/yb-core-snackbar-v3';

const GroupOperationsMonitor: React.FC = () => {
  const {
    lenderId: allocatorId,
    agencyId,
    caUserId: userId,
  } = useAppSelector((state) => state.userConfiguration);
  const { stats, jobs } = useAppSelector((state) => state.groupOperations);

  // State for completed job notification
  const [completedJobNotification, setCompletedJobNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
    taskType: string;
    allocationFileId: string;
  } | null>(null);

  const notifiedJobsRef = useRef<Set<string>>(new Set());

  const getTaskActionLabel = (taskType: string, tense: 'present' | 'past') => {
    switch (taskType) {
      case 'ASSIGNMENT':
        return tense === 'present' ? 'Assigning' : 'Assigned';
      case 'UNASSIGNMENT':
        return tense === 'present' ? 'Unassigning' : 'Unassigned';
      case 'REASSIGNMENT':
        return tense === 'present' ? 'Reassigning' : 'Reassigned';
      default:
        return tense === 'present' ? 'Processing' : 'Processed';
    }
  };

  const getInProgressMessage = (job: GroupOperationJob) => {
    const action = getTaskActionLabel(job.taskType, 'present');
    const accountText = job.totalBorrowers === 1 ? 'account' : 'accounts';
    return `${action} ${job.totalBorrowers} ${accountText} of ${
      job.allocationFileName
    } to ${job.groupName}: ${job.userName || 'User'}`;
  };

  const getCompletedMessage = (job: GroupOperationJob) => {
    const action = getTaskActionLabel(job.taskType, 'past');
    const accountText = job.totalBorrowers === 1 ? 'account' : 'accounts';
    return `${action} ${job.totalBorrowers} ${accountText} of ${
      job.allocationFileName
    } to ${job.groupName}: ${job.userName || 'User'}`;
  };

  const getFailedMessage = (job: GroupOperationJob) => {
    const action = getTaskActionLabel(job.taskType, 'present');
    const accountText = job.totalBorrowers === 1 ? 'account' : 'accounts';
    return `${action} ${job.totalBorrowers} ${accountText} of ${job.allocationFileName} to ${job.groupName} failed`;
  };

  useEffect(() => {
    const completedOrFailedJob = jobs.find((job) => {
      const jobKey = job.jobId || `${job.allocationFileId}-${job.taskType}`;
      const isFinished = job.status === 'COMPLETED' || job.status === 'FAILED';
      const notNotified = !notifiedJobsRef.current.has(jobKey);
      const isCurrentUser = job.userId === userId;
      return isFinished && notNotified && isCurrentUser;
    });

    if (completedOrFailedJob) {
      const jobKey =
        completedOrFailedJob.jobId ||
        `${completedOrFailedJob.allocationFileId}-${completedOrFailedJob.taskType}`;
      notifiedJobsRef.current.add(jobKey);

      const isSuccess = completedOrFailedJob.status === 'COMPLETED';
      const title = isSuccess
        ? getCompletedMessage(completedOrFailedJob)
        : getFailedMessage(completedOrFailedJob);

      setCompletedJobNotification({
        show: true,
        title,
        message: completedOrFailedJob.message || '',
        type: isSuccess ? 'success' : 'error',
        taskType: completedOrFailedJob.taskType,
        allocationFileId: completedOrFailedJob.allocationFileId,
      });
    }
  }, [jobs, userId]);

  useGroupOperationsSSE({
    agencyId: agencyId || '',
    allocatorId: allocatorId || '',
    userId: userId || '',
    enabled: true,
  });

  const inProgressJobs = useMemo(() => {
    return jobs.filter((job) => job.status === 'IN_PROGRESS');
  }, [jobs]);

  const accordionData = useMemo(() => {
    if (inProgressJobs.length === 0) {
      return [];
    }

    return [
      {
        title: `${inProgressJobs.length} ${
          inProgressJobs.length === 1 ? 'Task' : 'Tasks'
        } in progress`,
        subTitle: '',
        sideNote: '',
        badgeText: '',
        open: true,
        disabled: false,
        showIndicator: true,
        content: (
          <div className="flex flex-col gap-3 p-4">
            {inProgressJobs.map((job, index) => (
              <div key={`${job.allocationFileId}-${job.taskType}`}>
                <div
                  className="text-sm text-gray-700 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => {
                    const targetUrl = `/call/call-planner/${job.allocationFileId}`;
                    window.location.href = targetUrl;
                  }}
                  title="Click to view allocation in Call Planner"
                >
                  {getInProgressMessage(job)}
                </div>
                {index < inProgressJobs.length - 1 && (
                  <div className="border-b border-gray-200 mt-3"></div>
                )}
              </div>
            ))}
          </div>
        ),
      },
    ];
  }, [inProgressJobs]);

  return (
    <>
      {completedJobNotification?.show && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100]">
          <YbCoreSnackbar
            variant={'floated-without-shadow'}
            title={completedJobNotification.title}
            info={completedJobNotification.message || ''}
            type={completedJobNotification.type}
            infoType={'min'}
            showIcon={true}
            showClose={true}
            autoClose={false}
            onClose={() => setCompletedJobNotification(null)}
            customIcon={
              completedJobNotification.type === 'success'
                ? 'info-circle-filled'
                : 'alert-circle'
            }
            buttonText="Go to Page"
            onButtonClick={() => {
              if (completedJobNotification?.allocationFileId) {
                const targetUrl = `/call/call-planner/${completedJobNotification.allocationFileId}`;
                window.location.href = targetUrl;

                // Close notification after navigation
                setCompletedJobNotification(null);
              }
            }}
          />
        </div>
      )}

      {/* In-progress jobs accordion */}
      {inProgressJobs.length > 0 && (
        <div
          className={`fixed right-6 z-50 w-96 shadow-lg ${
            completedJobNotification?.show ? 'top-28' : 'top-20'
          }`}
        >
          <AccordionViewV3 data={accordionData} />
        </div>
      )}
    </>
  );
};

export default GroupOperationsMonitor;
